import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Textarea,
  Switch,
  FormHelperText,
  Select,
  Grid,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

const clusterSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  slug: z.string().max(255).optional(),
  cluster_code: z.string().max(255).optional(),
  parent_id: z.union([z.number(), z.nan(), z.null()]).optional(),
  descriptions: z.string().optional(),
  active: z.boolean(),
})

type ClusterFormData = z.infer<typeof clusterSchema>

interface ClusterFormProps {
  clusterId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

interface ClusterOption {
  id: number
  name: string
  label: string
  parent_id: number | null
  cluster_code?: string
}

export function ClusterForm({ clusterId, onSuccess, onCancel }: ClusterFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [parentOptions, setParentOptions] = useState<ClusterOption[]>([])
  const { showError, showSuccess } = useToastNotification()
  const isEditMode = !!clusterId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ClusterFormData>({
    resolver: zodResolver(clusterSchema),
    defaultValues: {
      name: '',
      slug: '',
      cluster_code: '',
      parent_id: null,
      descriptions: '',
      active: true,
    },
  })

  const activeValue = watch('active')

  useEffect(() => {
    const fetchTree = async () => {
      try {
        const res = await apiClient.get<{ data: ClusterOption[] }>('/options/clusters/tree')
        const list = res.data?.data ?? []
        setParentOptions(list)
      } catch (e) {
        showError(e)
      }
    }
    fetchTree()
  }, [showError])

  useEffect(() => {
    if (isEditMode && clusterId) {
      const fetch = async () => {
        setIsLoading(true)
        try {
          const res = await apiClient.get(`/organization/clusters/${clusterId}`)
          const d = res.data?.data ?? res.data ?? {}
          reset({
            name: d.name ?? '',
            slug: d.slug ?? '',
            cluster_code: d.cluster_code ?? '',
            parent_id: d.parent_id ?? null,
            descriptions: d.descriptions ?? '',
            active: d.active === 'Active',
          })
        } catch (e) {
          showError(e)
        } finally {
          setIsLoading(false)
        }
      }
      fetch()
    }
  }, [clusterId, isEditMode, reset, showError])

  const onSubmit = async (data: ClusterFormData) => {
    setIsSubmitting(true)
    try {
      const payload = {
        ...data,
        parent_id: data.parent_id && !Number.isNaN(Number(data.parent_id)) ? Number(data.parent_id) : null,
      }
      if (isEditMode && clusterId) {
        await apiClient.put(`/organization/clusters/${clusterId}`, payload)
        showSuccess('Success', 'Cluster updated successfully')
      } else {
        await apiClient.post('/organization/clusters', payload)
        showSuccess('Success', 'Cluster created successfully')
      }
      onSuccess?.()
    } catch (e) {
      showError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Box p={4}>
        <Text>Loading cluster...</Text>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input
              {...register('name')}
              placeholder="e.g. IT Cluster, Finance Cluster"
            />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Slug</FormLabel>
          <FormControl isInvalid={!!errors.slug}>
            <Input
              {...register('slug')}
              placeholder="e.g. it-cluster, finance-cluster"
            />
            <FormHelperText>Leave empty to auto-generate from name.</FormHelperText>
            <FormErrorMessage>{errors.slug?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Cluster Code</FormLabel>
          <FormControl isInvalid={!!errors.cluster_code}>
            <Input
              {...register('cluster_code')}
              placeholder="e.g. IT_CLUSTER"
            />
            <FormHelperText>Leave empty to auto-generate.</FormHelperText>
            <FormErrorMessage>{errors.cluster_code?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Parent Cluster</FormLabel>
          <FormControl isInvalid={!!errors.parent_id}>
            <Select
              {...register('parent_id', { setValueAs: (v) => (v === '' ? null : Number(v)) })}
              placeholder="— None (top-level) —"
            >
              {parentOptions
                .filter((c) => !clusterId || c.id !== clusterId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
            </Select>
            <FormErrorMessage>{errors.parent_id?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Descriptions</FormLabel>
          <FormControl isInvalid={!!errors.descriptions}>
            <Textarea
              {...register('descriptions')}
              placeholder="Optional description"
              rows={3}
            />
            <FormErrorMessage>{errors.descriptions?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Active</FormLabel>
          <FormControl>
            <Switch
              isChecked={activeValue}
              onChange={(e) => setValue('active', e.target.checked)}
            />
            <FormHelperText>When inactive, this cluster is hidden from selection.</FormHelperText>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Saving...' : 'Creating...'}>
            {isEditMode ? 'Save' : 'Create Cluster'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
